// FeeManager.js
import formatCurrency from "constants/formatCurrency";
import formatDate from "constants/formatDate";
import { addFee, deleteFee, editFee } from "helpers/fakebackend_helper";
import React, { useState } from "react";
import {
    Button,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    InputGroup,
    InputGroupText,
} from "reactstrap";

const feeTypes = [
    "Management Fee",
    "Parking Lot Fee",
    "Insurance Fee",
    "Permit Meter Fee",
    "Tech Fee",
    "Ops Supplies Fee",
    "Other Fees",
];

const feeTypeToLocationKey = {
    "Management Fee": "management_fee",
    "Parking Lot Fee": "parking_lot_fee",
    "Insurance Fee": "insurance_fee",
    "Permit Meter Fee": "permit_meter_fee",
    "Tech Fee": "tech_fee",
    "Ops Supplies Fee": "ops_supplies_fee",
};

const FeeManager = ({ location_id, location, fees, handleSave }) => {
    const user = JSON.parse(localStorage.getItem("authUser"));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingFee, setEditingFee] = useState(null);
    const [formData, setFormData] = useState({
        type: "Management Fee",
        payment_date: new Date().toISOString().slice(0, 10), // yyyy-mm-dd
        amount: location.management_fee,
        note: "",
    });

    const toggleModal = () => setIsModalOpen(!isModalOpen);

    const handleAddClick = () => {
        setEditingFee(null);
        setFormData({
            type: "Management Fee",
            payment_date: new Date().toISOString().slice(0, 10),
            amount: location.management_fee,
            note: "",
        });
        toggleModal();
    };

    const handleEditClick = (fee) => {
        setEditingFee(fee);
        setFormData({
            type: fee.type,
            payment_date: fee.payment_date,
            amount: fee.amount,
            note: fee.note || "",
        });
        toggleModal();
    };

    const handleDeleteClick = async (id) => {
        await deleteFee(user.token, id);
        handleSave();
    };

    const onSave = async () => {
        if (editingFee) {
            await editFee(user.token, editingFee.id, formData);
        } else {
            const newFee = {
                location_id,
                ...formData,
            };
            await addFee(user.token, newFee);
        }
        toggleModal();
        handleSave();
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 style={{ color: "#FFCA5C", fontWeight: "bold" }}>Fees/Billables</h5>
                <Button className="button-gradient-bg mb-3" onClick={handleAddClick}>
                    Add Fee / Billable
                </Button>
            </div>

            <Table className="align-middle table-hover table-dark table-nowrap mb-0">
                <thead className="table-success">
                    <tr>
                        <th>Type</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Note</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {fees && fees.length > 0 ? (
                        fees.map((fee) => (
                            <tr key={fee.id}>
                                <td>{fee.type}</td>
                                <td>{formatDate(fee.payment_date)}</td>
                                <td>${formatCurrency(fee.amount)}</td>
                                <td>{fee.note || ""}</td>
                                <td>
                                    <Button
                                        color="link"
                                        className="p-0 me-2"
                                        onClick={() => handleEditClick(fee)}
                                    >
                                        <i className="bx bx-pencil"></i>
                                    </Button>
                                    <Button
                                        color="link"
                                        className="p-0 text-danger"
                                        onClick={() => handleDeleteClick(fee.id)}
                                    >
                                        <i className="bx bx-trash"></i>
                                    </Button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={5} className="text-center text-muted">
                                No fees recorded
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>

            {/* Add/Edit Modal */}
            <Modal isOpen={isModalOpen} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>
                    {editingFee ? "Edit Fee" : "Add Fee"}
                </ModalHeader>
                <ModalBody>
                    <label>Type</label>
                    <Input
                        type="select"
                        value={formData.type}
                        onChange={(e) => {
                            const newType = e.target.value;
                            const key = feeTypeToLocationKey[newType];
                            const newAmount = key && location ? location[key] || 0 : formData.amount;

                            setFormData({
                                ...formData,
                                type: newType,
                                amount: newAmount,
                            });
                        }}
                    >
                        {feeTypes.map((ft) => (
                            <option key={ft} value={ft}>
                                {ft}
                            </option>
                        ))}
                    </Input>

                    <label className="mt-3">Date</label>
                    <Input
                        type="date"
                        value={formData.payment_date}
                        onChange={(e) =>
                            setFormData({ ...formData, payment_date: e.target.value })
                        }
                    />

                    <label className="mt-3">Amount</label>
                    <InputGroup>
                        <InputGroupText>$</InputGroupText>
                        <Input
                            type="number"
                            value={formData.amount}
                            onChange={(e) =>
                                setFormData({ ...formData, amount: e.target.value })
                            }
                        />
                    </InputGroup>

                    <label className="mt-3">Note (optional)</label>
                    <Input
                        type="text"
                        value={formData.note}
                        onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                        placeholder="Optional note"
                    />
                </ModalBody>
                <ModalFooter>
                    <Button className="button-gradient-bg" onClick={onSave}>
                        Save
                    </Button>
                    <Button color="secondary" onClick={toggleModal}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default FeeManager;